// FocusRuleModal — create / edit form for focus_rules. Replaces the
// "Seed via SQL for the V1 demo" placeholder previously surfaced on the
// Focus page. Owner-driven INSERT / UPDATE / DELETE go through the
// focus_rules RLS policies (migration 0017 — is_owner gate).
//
// Schema constraints surfaced in form validation:
//   - label non-blank (DB has no constraint; we enforce client-side
//     because it's the visible identity of the rule)
//   - day_of_week one of 0..6 (radio group; can only pick valid values)
//   - end_time > start_time (DB CHECK focus_rules_end_after_start)
//   - timezone non-blank (DB DEFAULT 'America/Los_Angeles' but we show
//     an explicit picker so the user knows which tz the rule lives in)
//
// Cross-midnight blocks NOT supported in V1 (per ADR-0012 §5.2). The
// form rejects end <= start; user is told to split.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export type EditableFocusRule = {
  id: string;
  day_of_week: number;
  start_time: string; // 'HH:MM' or 'HH:MM:SS'
  end_time: string;
  timezone: string;
  label: string;
  is_active: boolean;
  category_allowlist: string[];
};

type FocusRuleModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Existing rule to edit; undefined → create mode.
  rule?: EditableFocusRule;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A short curated list. Browser tz prefilled by default; user can paste
// any IANA name. We don't enumerate the full IANA tz database (~600
// entries) — that's a picker UX problem of its own.
const COMMON_TZS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Australia/Sydney',
];

// Page-wide display tz is pinned to PT on /focus (see DISPLAY_TZ in
// pages/Focus.tsx). The modal defaults to PT too so a new rule lands
// in the same tz the rest of the page renders in — avoids the demo
// confusion where a creator in IST sees their new rule fail to line
// up with the Today timeline. Per-rule tz is still editable via the
// combobox below for multi-tz future users.
function defaultTz(): string {
  return 'America/Los_Angeles';
}

function trimSeconds(hms: string): string {
  // 'HH:MM:SS' → 'HH:MM' for the <input type="time"> default value.
  return hms.length >= 5 ? hms.slice(0, 5) : hms;
}

export default function FocusRuleModal({
  open,
  onOpenChange,
  rule,
}: FocusRuleModalProps): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isEdit = !!rule;

  const [label, setLabel] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('11:00');
  const [timezone, setTimezone] = useState<string>(defaultTz());
  const [error, setError] = useState<string | null>(null);

  // Reset form state whenever the modal opens (avoids stale data
  // bleeding from one edit into the next).
  useEffect(() => {
    if (!open) return;
    if (rule) {
      setLabel(rule.label);
      setDayOfWeek(rule.day_of_week);
      setStartTime(trimSeconds(rule.start_time));
      setEndTime(trimSeconds(rule.end_time));
      setTimezone(rule.timezone || defaultTz());
    } else {
      setLabel('');
      setDayOfWeek(new Date().getDay()); // default to today
      setStartTime('09:00');
      setEndTime('11:00');
      setTimezone(defaultTz());
    }
    setError(null);
  }, [open, rule]);

  const upsert = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not signed in.');
      const trimmedLabel = label.trim();
      if (!trimmedLabel) throw new Error('Label cannot be empty.');
      if (!timezone.trim()) throw new Error('Pick a timezone.');
      if (startTime >= endTime) {
        throw new Error('End time must be later than start time. Split cross-midnight blocks.');
      }

      const payload = {
        owner_id: user.id,
        day_of_week: dayOfWeek,
        start_time: startTime,
        end_time: endTime,
        timezone: timezone.trim(),
        label: trimmedLabel,
        is_active: true,
        category_allowlist: rule?.category_allowlist ?? [],
      };

      if (isEdit && rule) {
        const { error: updErr } = await supabase
          .from('focus_rules')
          .update({
            day_of_week: payload.day_of_week,
            start_time: payload.start_time,
            end_time: payload.end_time,
            timezone: payload.timezone,
            label: payload.label,
          })
          .eq('id', rule.id);
        if (updErr) throw updErr;
      } else {
        const { error: insErr } = await supabase.from('focus_rules').insert(payload);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus_rules'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!rule) return;
      // Soft-delete (consistent with the rest of the codebase). RLS
      // policy allows owner UPDATE; the page-level fetcher filters on
      // deleted_at IS NULL so the row disappears from the UI.
      const { error: delErr } = await supabase
        .from('focus_rules')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', rule.id);
      if (delErr) throw delErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus_rules'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit focus rule' : 'New focus rule'}</DialogTitle>
          <DialogDescription>
            Recurring block the Guardian agent defends. Times are local in the rule&apos;s timezone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="rule-label" className="text-xs uppercase tracking-wide">
              Label
            </Label>
            <Input
              id="rule-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Deep Work"
              maxLength={80}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide">Day of week</Label>
            <div
              role="radiogroup"
              aria-label="Day of week"
              className="mt-1 grid grid-cols-7 gap-1.5"
            >
              {DAY_NAMES.map((d, idx) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={dayOfWeek === idx}
                  onClick={() => setDayOfWeek(idx)}
                  className={`text-xs py-2 rounded-md transition-colors ${
                    dayOfWeek === idx
                      ? 'bg-forest text-primary-foreground shadow-sm'
                      : 'bg-secondary text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rule-start" className="text-xs uppercase tracking-wide">
                Start
              </Label>
              <Input
                id="rule-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="rule-end" className="text-xs uppercase tracking-wide">
                End
              </Label>
              <Input
                id="rule-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="rule-tz" className="text-xs uppercase tracking-wide">
              Timezone
            </Label>
            <Input
              id="rule-tz"
              list="common-tz-list"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="America/Los_Angeles"
              className="mt-1 font-mono"
            />
            <datalist id="common-tz-list">
              {COMMON_TZS.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </div>

          {error && (
            <p className="text-xs text-alert" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          {isEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (window.confirm('Delete this focus rule? Existing audit trail is preserved.')) {
                  remove.mutate();
                }
              }}
              disabled={remove.isPending || upsert.isPending}
              className="text-alert hover:text-alert"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          )}
          <div className="flex items-center gap-2 sm:ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending || remove.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => upsert.mutate()}
              disabled={upsert.isPending || remove.isPending}
            >
              {upsert.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {isEdit ? 'Save' : 'Create rule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
